import React, { FC, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IState } from "store/store.interface";
import FilterIcon from "@material-ui/icons/FilterList";
import {
  addBundleFilters,
  removeBundleFilters,
  resetBundleFilters,
} from "store/actions/bundlePromotion.actions";
import { Button, Box, Select, Text, Dialog, Chip } from "common";
import { IconButton, useMediaQuery, useTheme } from "@material-ui/core";

const BundleFilters = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);

  const { selectOptions, filters } = useSelector((state: IState) => ({
    ...state.config,
    ...state.bundlePromotion,
    ...state.auth,
  }));

  const permittedPromotions =
    selectOptions.bundlePromotionTypes.filter(
      (campaign) => campaign.value !== "CREATE_BULK_CAMPAIGN",
    ) || [];

  const dispatch = useDispatch();

  const onOpenModal = () => {
    setOpen(true);
  };

  const onCloseModal = () => {
    setOpen(false);
  };

  const onChangeStatus = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(addBundleFilters({ name: "Status", value: event.target.value }));
    },
    [dispatch],
  );

  const onChangeTypes = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value === "COUPON") {
        dispatch(
          addBundleFilters({ name: "Object", value: event.target.value }),
        );
      } else {
        dispatch(addBundleFilters({ name: "type", value: event.target.value }));
      }
    },
    [dispatch],
  );

  const onClearFilter = useCallback(
    (data: any) => () => {
      dispatch(removeBundleFilters({ name: data.label, value: data.value }));
    },
    [dispatch],
  );

  const onResetFilters = useCallback(() => {
    dispatch(resetBundleFilters());
  }, [dispatch]);

  const list = useMemo(() => {
    let updatedList = [] as any;
    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        updatedList = [
          ...updatedList,
          ...filter.value.map((value) => {
            return {
              label: filter.name,
              value: `${value}`,
            };
          }),
        ];
      });
      return updatedList;
    }
  }, [filters]);

  const filtersCapsules = useMemo(() => {
    return (
      <>
        {list && list.length > 0 && (
          <Box display="flex" flexDirection="column">
            <Box display="flex" alignItems="center" margin="40px 0px 0px">
              <Button
                data-testid="clear-filters-button"
                variant="outlined"
                padding="10px"
                round
                minWidth="135px"
                margin="0 10px 0 0"
                onClick={onResetFilters}
                color="primary"
                size="medium"
                height="38px"
              >
                Clear filters
              </Button>
              <Box display="flex" flexWrap="nowrap" overflow="scroll">
                {list.map(({ label, value }: any) => (
                  <Chip
                    key={`${label}-${value}`}
                    label={
                      <Text mx="8px" fontWeight={600}>
                        {value}
                      </Text>
                    }
                    icon={
                      <Text fontSize="12px" margin="0px" color="text.secondary">
                        {label}:
                      </Text>
                    }
                    onDelete={onClearFilter({ label, value })}
                    borderRadius="18.5px"
                    backgroundColor="#fff"
                    border="1px solid #CFCFCF"
                    height="32px"
                    margin="0 10px 0 0"
                    data-testid="tag-wrapper"
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}
        {isMobile && (
          <Box marginTop="15px">
            <Button
              onClick={onCloseModal}
              variant="contained"
              round
              height="38px"
              color="secondary"
            >
              {list && list.length > 0 ? "Apply" : "Close"}
            </Button>
          </Box>
        )}
      </>
    );
  }, [list, onClearFilter, isMobile, onResetFilters]);

  const filtersFileds = useMemo(() => {
    return (
      <Box
        gridTemplateColumns={isMobile ? "1fr" : "repeat(4, 1fr)"}
        width={["100%", "100%", "70%", "70%"]}
        gridGap="10px"
      >
        <Select
          options={selectOptions["statusTypes"]}
          onNativeChange={onChangeStatus}
          label="Status"
          nativeValue=""
          stickyLabel={true}
          width="100%"
          size="small"
          variant="native"
          inputVariant="outlined"
          testId="filter-select-status"
        />
        <Select
          options={permittedPromotions}
          label="Type"
          stickyLabel={true}
          nativeValue=""
          onNativeChange={onChangeTypes}
          width="100%"
          size="small"
          variant="native"
          inputVariant="outlined"
        />
      </Box>
    );
  }, [
    selectOptions,
    isMobile,
    onChangeStatus,
    onChangeTypes,
    permittedPromotions,
  ]);

  return (
    <Box display="flex" flexDirection="column" width="100%">
      {isMobile && open && (
        <Dialog open={open} onBackdropClick={onCloseModal} padding="20px">
          <Box>{filtersFileds}</Box>
          {filtersCapsules}
        </Dialog>
      )}
      <Box
        display="flex"
        justifyContent={isMobile ? "space-between" : "flex-start"}
        alignItems="center"
        margin="20px 0px 0px"
      >
        <Text
          fontSize="16px"
          fontWeight={500}
          whiteSpace="nowrap"
          marginRight="20px"
        >
          Filter By
        </Text>
        {isMobile ? (
          <IconButton onClick={onOpenModal}>
            <FilterIcon color="primary" />
          </IconButton>
        ) : (
          filtersFileds
        )}
      </Box>
      {!isMobile ? filtersCapsules : null}
    </Box>
  );
};

export default BundleFilters;
